import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import { map, take } from 'rxjs';
import AppState from '../../app.state';
import { SessionRehydratedEvent } from '../../features/auth/state-management/auth.events';
import { selectAuthorizationToken } from '../../features/auth/state-management/auth.selectors';
import { AppRoutes } from '../constants/routes.constants';
import { SessionStorageService } from '../services/session-storage.service';

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

export const authGuard: CanActivateFn = () => {
  const store = inject(Store<AppState>);
  const router = inject(Router);
  const sessionStorageService = inject(SessionStorageService);

  return store.select(selectAuthorizationToken).pipe(
    take(1),
    map((token) => {
      if (token) {
        return true;
      }

      const data = sessionStorageService.load();

      if (data) {
        try {
          const decoded = jwtDecode<JwtPayload>(data.authorizationToken);

          if (typeof decoded.exp === 'number' && decoded.exp > Date.now() / 1000) {
            store.dispatch(
              SessionRehydratedEvent({
                authenticationToken: data.authenticationToken,
                authorizationToken: data.authorizationToken,
                resources: data.resources,
                selectedUserKind: data.selectedUserKind,
              })
            );
            return true;
          }
        } catch {
          // JWT malformed — fall through to clear and redirect
        }

        sessionStorageService.clear();
      }

      router.navigate([`/${AppRoutes.LOGIN}`]);
      return false;
    })
  );
};
